import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateChildFn, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { AuthService } from '../auth.service';
import { of, switchMap } from 'rxjs';

export const AuthGuard: CanActivateFn | CanActivateChildFn = (route, state) => {
    const router: Router = inject(Router);
    const authService: AuthService = inject(AuthService);

    const token = route.queryParamMap.get('token');

    if (token) {
        authService.signOut(false); // Clear the auth session but keep UI presentation preferences.
        return authService.validateTokenFromUrlAndCreateNew(token).pipe(
            switchMap(success => {
                if (success) {
                    return of(checkAccess(route, state, authService, router));
                }
                return of(router.createUrlTree(['/sign-in'], { queryParams: { returnUrl: state.url } }));
            })
        );
    }

    // Redirect unauthenticated users
    else if (!authService.isAuthenticated) {
        return of(router.createUrlTree(['/sign-in'], { queryParams: { returnUrl: state.url } }));
    }

    return checkAccess(route, state, authService, router);
};

function checkAccess(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
    authService: AuthService,
    router: Router
): boolean {
    const parentData = route.data as any;
    const childData = route.firstChild?.data as any;

    const pickAuthData = (d?: any) => {
        if (!d) return undefined;

        const hasRoles = Array.isArray(d.roles) && d.roles.length > 0;
        const hasClaims = Array.isArray(d.claims) && d.claims.length > 0;

        return (hasRoles || hasClaims) ? d : undefined;
    };

    // Prefer parent auth data; otherwise fall back to firstChild auth data
    const rawAuthData = pickAuthData(parentData) ?? pickAuthData(childData);

    // No auth requirements => allow
    if (!rawAuthData) {
        return true;
    }

    const mandatId = authService.currentMandat?.Id ?? '';

    const routeGuardData = {
        ...rawAuthData,
        mandatId
    };

    if (!authService.canAccessRoute(routeGuardData)) {
        router.navigate(['/404-not-found'], { queryParams: { returnUrl: state.url } });
        return false;
    }

    return true;
}
